import React, {useEffect, useState} from 'react';

import i18next from 'i18next';
import {Route, useRouteMatch} from 'react-router-dom';

import {ReactComponent as HumanIcon} from 'assets/svg/human.svg';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {DatePicker} from 'components/ui-kit/DatePicker';
import {DropdownMenu} from 'components/ui-kit/DropdownMenu';
import {Form, useForm} from 'components/ui-kit/Form';
import {Hint} from 'components/ui-kit/Hint';
import {Icon} from 'components/ui-kit/Icon';
import {IconButton} from 'components/ui-kit/IconButton';
import {Input} from 'components/ui-kit/Input';
import {Label} from 'components/ui-kit/Label';
import {Link} from 'components/ui-kit/Link';
import {Modal} from 'components/ui-kit/Modal';
import {RadioButton, RadioButtonOption} from 'components/ui-kit/RadioButton';
import {RouterSwitch, RouterTab, RouterTabList} from 'components/ui-kit/RouterTabs';
import {Select} from 'components/ui-kit/Select';
import {Stepper} from 'components/ui-kit/Stepper';
import {Table} from 'components/ui-kit/Table';
import {Tag, Tags} from 'components/ui-kit/Tags';
import {Textarea} from 'components/ui-kit/Textarea';
import {Title} from 'components/ui-kit/Title';
import {Checkbox} from 'components/ui-kit/Сheckbox';
import {openSidebar} from 'models/mobileSidebar';
import {formatDate} from 'utils/date';

import {CellContainer, FlexLayout, Header} from './styled';

const selectOptions = [
    {value: null, label: 'Все'},
    {value: 1, label: 'Первый'},
    {value: 2, label: 'Второй'},
    {value: 3, label: 'Третий'},
];

const fieldsConfig = {
    email: {
        defaultValue: 'tours@blizzard.com',
        validation: {
            required: 'Обязательное поле',
            minLength: {
                value: 10,
                message: 'Максимальная длина поля - 10 символов',
            },
            pattern: {
                value: /^\S+@\S+$/i,
                message: 'Введите валидный email',
            },
        },
    },
    number: {
        defaultValue: selectOptions[1],
        validation: {
            validate: (data: any) => (data.value == null ? 'Обязательное поле' : true),
        },
    },
    date: {
        defaultValue: null,
        validation: {
            required: 'Обязательное поле',
        },
    },
    text: {
        defaultValue: '',
        validation: {
            required: 'Обязательное поле',
        },
    },
};

const steps = [
    {
        label: 'Шаг 1. Общие данные',
        component: <h2>First step</h2>,
    },
    {
        label: 'Шаг 2. Товары',
        component: <h2>Second step</h2>,
    },
    {
        label: 'Шаг 3. Еще это',
        component: <h2>Third step</h2>,
    },
    {
        label: 'Шаг 4. И вот это',
        component: <h2>Forth step</h2>,
    },
];

const AlignCell = ({value}: {value: string}) => <CellContainer>{value}</CellContainer>;

const columns = [
    {
        Header: 'Column 1',
        accessor: 'column1',
        Cell: AlignCell,
        width: 510,
    },
    {
        Header: 'Column 2',
        accessor: 'column2',
        Cell: AlignCell,
        width: 240,
    },
    {
        Header: 'Column 3',
        accessor: 'column3',
        Cell: AlignCell,
        width: 240,
    },
    {
        Header: 'Column 4',
        id: 'column4',
        disableSortBy: true,
        Cell: ({row}: any) => <AlignCell value={row.original.column4 + ' ***'} />,
        width: 240,
    },
];

const data = [
    {
        column1: 0,
        column2: 'Test 1',
        column3: 'Test 1',
        column4: 'Test 1',
    },
    {
        column1: 1,
        column2: 'Test 2',
        column3: 'Test 2',
        column4: 'Test 2',
    },
    {
        column1: 2,
        column2: 'Test 3',
        column3: 'Test 3',
        column4: 'Test 3',
    },
    {
        column1: 3,
        column2: 'Test 4',
        column3: 'Test 4',
        column4: 'Test 4',
    },
    {
        column1: 4,
        column2: 'Test 5',
        column3: 'Test 5',
        column4: 'Test 5',
    },
];

const radioOptions: RadioButtonOption[] = [
    {
        value: 'value1',
        label: 'value 1 (title)',
    },
    {
        value: 'value2',
    },
    {
        value: 'value3',
        label: 'value 3 with callback',
        onChange: (item) => console.log(`onActivate: ${item}`),
    },
];

export function TestPage() {
    const formData = useForm();
    const {errors} = formData;
    const {path, url} = useRouteMatch();
    const [step, setStep] = useState(1);
    const [checkbox, setCheckbox] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [radioValue, setRadioValue] = useState({label: 'Test', value: 'TEST'});

    useEffect(() => {
        const timer = setTimeout(() => {
            setRadioValue({label: 'Test', value: 'TEST'});
        }, 2000);
        return () => clearTimeout(timer);
    });

    const hasErrors = () => Object.keys(errors).length > 0;
    const onSubmit = (data: Record<string, any>) => console.log('Submit:', data);

    return (
        <>
            <Title size={2}>Test page</Title>

            <Header>Localization</Header>
            <>
                <FlexLayout>
                    <Button
                        variant={ButtonVariant.Outline}
                        onClick={() => i18next.changeLanguage('ru')}
                        disabled={i18next.language === 'ru'}
                    >
                        RU
                    </Button>
                    <Button
                        variant={ButtonVariant.Outline}
                        onClick={() => i18next.changeLanguage('enUs')}
                        disabled={i18next.language === 'enUs'}
                    >
                        EN-US
                    </Button>
                </FlexLayout>
                <div>{formatDate(new Date(), 'dd MMMMMMM yyyy')}</div>
            </>

            <Header>Radio Button</Header>
            <>
                <p>Controlled</p>
                <RadioButton
                    options={radioOptions}
                    direction={'horizontal'}
                    value={radioValue}
                    onChange={(val) => console.log(`radio button change`, val)}
                />
                <p>Uncontrolled</p>
                <RadioButton
                    options={[...radioOptions]}
                    direction={'vertical'}
                    onChange={(val) => console.log(`radio button change`, val)}
                />
            </>

            <Header>Buttons</Header>
            <>
                <FlexLayout>
                    <Button variant={ButtonVariant.Normal}>Hello</Button>
                    <Button variant={ButtonVariant.Outline}>Hello</Button>
                    <Button variant={ButtonVariant.Text}>Hello</Button>
                </FlexLayout>
                <FlexLayout>
                    <Button variant={ButtonVariant.Normal} disabled>
                        Hello
                    </Button>
                    <Button variant={ButtonVariant.Outline} disabled>
                        Hello
                    </Button>
                    <Button variant={ButtonVariant.Text} disabled>
                        Hello
                    </Button>
                </FlexLayout>
                <FlexLayout>
                    <Button variant={ButtonVariant.Normal} isLoading>
                        Hello
                    </Button>
                    <Button variant={ButtonVariant.Outline} isLoading>
                        Hello
                    </Button>
                    <Button variant={ButtonVariant.Text} isLoading>
                        Hello
                    </Button>
                </FlexLayout>
            </>

            <Header>Form</Header>
            <>
                <Form
                    formData={formData}
                    onSubmit={onSubmit}
                    fieldsConfig={fieldsConfig}
                    maxFieldsDepth={2}
                >
                    <Hint text={errors.email && errors.email.message} isError>
                        <Input
                            label="Input"
                            formFieldName="email"
                            placeholder="E-mail"
                            hasError={!!errors.email}
                        />
                    </Hint>

                    <Hint text={errors.number && errors.number.message} isError>
                        <Select
                            label="Select"
                            formFieldName="number"
                            isSearchable={false}
                            menuPlacement="auto"
                            options={selectOptions}
                            hasError={!!errors.number}
                        />
                    </Hint>

                    <Hint text={errors.date && errors.date.message} isError>
                        <DatePicker
                            label="Datepicker"
                            formFieldName="date"
                            selectsEnd
                            hasError={!!errors.date}
                            locale={i18next.language}
                        />
                    </Hint>

                    <Hint text={errors.text && errors.text.message} isError>
                        <Textarea label="Textarea" formFieldName="text" hasError={!!errors.text} />
                    </Hint>

                    <Button type="submit" disabled={hasErrors()}>
                        Подтвердить
                    </Button>
                </Form>
            </>

            <Header>Router tabs</Header>
            <>
                <RouterTabList baseUrl={url}>
                    <RouterTab url="">Пользователи</RouterTab>
                    <RouterTab url="roles">Роли</RouterTab>
                    <RouterTab url="roles2">Еще это</RouterTab>
                    <RouterTab url="roles3">И вот это</RouterTab>
                    <RouterTab url="roles4">Пятое</RouterTab>
                    <RouterTab url="roles5">Десятое</RouterTab>
                </RouterTabList>

                <RouterSwitch rootPath={path}>
                    <Route path={`${path}`} exact>
                        <Title size={3}>Users</Title>
                    </Route>
                    <Route path={`${path}/roles`} exact>
                        <Title size={3}>Roles</Title>
                    </Route>
                    <Route path={`${path}/roles2`} exact>
                        <Title size={3}>Roles 2</Title>
                    </Route>
                </RouterSwitch>
            </>

            <Header>Stepper</Header>
            <>
                <button disabled={step === 0} type="button" onClick={() => setStep(step - 1)}>
                    Prev
                </button>
                <button
                    disabled={step === steps.length - 1}
                    type="button"
                    onClick={() => setStep(step + 1)}
                    style={{marginBottom: '30px'}}
                >
                    Next
                </button>

                <Stepper steps={steps} activeStep={step} />
            </>

            <Header>Modal</Header>
            <>
                <button type="button" onClick={() => setShowModal(!showModal)}>
                    Show/hide modal
                </button>

                <Modal
                    title="Загрузка файла"
                    onClose={() => setShowModal(false)}
                    show={showModal}
                    footer={<h5>Simple footer</h5>}
                >
                    <div
                        className="test"
                        style={{backgroundColor: '#ccc', height: '250px', marginBottom: '20px'}}
                    >
                        <p>Hello world!</p>
                    </div>
                    <Button>Submit</Button>
                </Modal>
            </>

            <Header>Table</Header>
            <>
                <Table
                    pageCount={5}
                    availablePageSizes={[10, 30, 50]}
                    pageIndex={0}
                    data={data}
                    columns={columns}
                />
            </>

            <Header>Checkbox</Header>
            <>
                <Checkbox checked={checkbox} onChange={(e) => setCheckbox(e.currentTarget.checked)}>
                    Тык!
                </Checkbox>
            </>

            <Header>Tags</Header>
            <>
                <Tags>
                    <Tag>Simple tag</Tag>
                    <Tag>Test tag test tag</Tag>
                </Tags>
            </>

            <Header>Title(s)</Header>
            <>
                <Title size={1}>Title 1</Title>
                <Title size={2}>Title 2</Title>
                <Title size={3}>Title 3</Title>
                <Title size={4}>Title 4</Title>
            </>

            <Header>Link</Header>
            <>
                <Link to="/test">Link</Link>
            </>

            <Header>Dropdown menu</Header>
            <>
                <DropdownMenu label="Test button">
                    <DropdownMenu.List>
                        <DropdownMenu.Item
                            key="edit"
                            onSelect={() => {
                                console.log('edit');
                            }}
                        >
                            Изменить
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                            key="copy"
                            onSelect={() => {
                                console.log('copy');
                            }}
                        >
                            Создать копию
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                            key="copy"
                            onSelect={() => {
                                console.log('copy');
                            }}
                        >
                            Третий вариант
                        </DropdownMenu.Item>
                    </DropdownMenu.List>
                </DropdownMenu>
            </>

            <Header>Label</Header>
            <>
                <Label text="Test label">
                    <Input />
                </Label>
            </>

            <Header>IconButton</Header>
            <>
                <IconButton
                    hoverIndication
                    icon={<Icon width={12} height={12} source={HumanIcon} />}
                    label="Hello"
                />
            </>

            <Header>Mobile Sidebar</Header>
            <Button onClick={() => openSidebar()}>Open</Button>
        </>
    );
}
